<!-- Project: GSSC Pilot Front-end -->
<!-- Filename: CommonListing.vue -->
<!-- Copyright European Space Agency, 2020 -->
<!-- This file is part of the source code package for contract PO PO 5001024866 -->
<!-- No part of the package, including this file, may be copied, modified,      -->
<!-- propagated, or distributed without prior approval by ESA.                  -->
<template>
  <div class="elements">
    <div class="elements__controls clearfix mb-3" v-if="elements.length">
      <form class="form-inline justify-content-between">
        <div class="form-group">
          <!-- Toggle list/grid view mode -->
          <button
            title="Cards/Table view"
            type="button"
            class="elements__toggle-view btn-reset"
            @click="toggleViewMode"
          >
            <i class="icon text-primary" :class="clsIconViewMode"></i>
          </button>
          <!-- Column selector button dropdown -->
          <div v-if="viewMode == 'list'" title="Show/Hide columns" class="dropdown">
            <button
              id="dropdown-columns"
              class="elements__controls-show-columns btn-reset"
              type="button"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
              data-flip="false"
            >
              <i class="icon icon-columns text-primary font-24"></i>
            </button>
            <div class="dropdown-menu">
              <a class="elements__controls-show-all-columns dropdown-item" href="#" @click.prevent="showAllColumns"
                >All</a
              >
              <asset-column-selector :filters="columnSelectors" v-model="selectedColumns" />
            </div>
          </div>
        </div>
        <div class="form-group">
          <!-- Download CSV metadata -->
          <button
            type="button"
            v-if="enableEdition"
            class="elements__download-csv btn-reset ml-1"
            title="Download CSV"
            @click.prevent="downloadCsv"
          >
            <i class="icon icon-csv text-primary font-24"></i>
          </button>
          <!-- Add element button -->
          <button
            type="button"
            v-if="enableEdition"
            class="elements__add btn-reset ml-1"
            title="Add"
            @click.prevent="addElement"
          >
            <i class="icon icon-add text-primary font-24"></i>
          </button>
          <!-- Sync elements button -->
          <button
            type="button"
            v-if="enableEdition"
            class="elements__sync btn-reset ml-1"
            title="Sync"
            @click.prevent="syncModal"
          >
            <i class="icon icon-loop text-primary font-24"></i>
          </button>
        </div>
      </form>
    </div>
    <!-- LIST MODE -->
    <div v-if="viewMode == 'list'" class="elements__results-list">
      <table class="table wrapped">
        <thead>
          <draggable v-model="userMovedColumns" draggable=".draggable" tag="tr">
            <th></th>
            <th v-for="col in allButFixedColumns" :key="col" class="align-middle draggable">
              <!-- button to toggle sort -->
              <button
                v-if="col in sortColumnIcons"
                @click.prevent="toggleColumnSort(col)"
                type="button"
                class="elements__sort btn-reset btn-sm align-middle"
              >
                <i class="icon text-primary px-0 ml-n1" :class="sortColumnIcons[col].icon"></i>
              </button>
              <span>{{ col }}</span>
            </th>
          </draggable>
        </thead>
        <tbody>
          <element-listing
            :data="element"
            v-for="element in elements"
            :key="element.id"
            :displayedColumns="allButFixedColumns"
            :catalogueFields="catalogueFields"
            @openEditModal="editElement"
            @openDeleteModal="confirmElementDeletion"
            @openNewAssetModal="openNewAssetModal"
            :enableEdition="enableEdition"
            :enableAddToElement="enableAddToElement"
            :arrayFields="arrayFields"
          >
          </element-listing>
        </tbody>
      </table>
    </div>
    <!-- GRID MODE -->
    <div v-else>
      <div class="row">
        <div class="col-md-6 col-xl-4 col-xxl-3 mt-3" v-for="element in elements" :key="element.id">
          <element-card
            :data="element"
            :titleField="dataType === 'Dataset' ? element.dataset_name : element.name"
            :displayedFields="selectableColumns"
            :enableEdition="enableEdition"
            @openEditModal="editElement"
            @openDeleteModal="confirmElementDeletion"
            :catalogueFields="catalogueFields"
            :arrayFields="arrayFields"
          ></element-card>
        </div>
      </div>
    </div>
    <!-- modal for downloadCsv, Sync, createOrEdit -->
    <message-modal
      class="message-modal"
      :title="modalTitle"
      :message="modalMessage"
      :okButtonText="okButtonText"
      :cancel="modalCancel"
    />
    <delete-message-modal
      class="delete-message-modal"
      :title="modalTitle"
      :message="modalMessage"
      :okButtonText="okButtonText"
      @confirmed="deleteElement"
    />
    <element-form-modal
      v-if="showElementModal"
      ref="elementFormRef"
      class="element-form-modal"
      :mode="elementModalMode"
      :dataType="dataType"
      :title="elementModalTitle"
      :catalogueFields="catalogueFields"
      :initData="editElementData"
      @closeFormModal="formModalClosed"
      @createOrEditNewElement="createOrEditNewElement"
      :arrayFields="arrayFields"
    />
    <new-asset-modal ref="newAssetRef" class="new-asset-modal" :data="datasetData" @assetSaved="showAssetSavedResult" />
    <sync-modal class="sync-modal" @closeModal="" @sync="sync" ref="syncModalRef"></sync-modal>
  </div>
</template>

<script>
import AssetColumnSelector from '@/components/AssetColumnSelector.vue'
import ElementListing from '@/components/ElementListing.vue'
import ColumnSelectorModel from '@/models/column_selector'
import MessageModal from '@/components/MessageModal'
import ElementFormModal from '@/components/ElementFormModal'
import NewAssetModal from '@/components/NewAssetModal'
import SyncModal from '@/components/SyncModal'
import ElementCard from '@/components/ElementCard'
import Vue from 'vue'
import * as api from '@/lib/api'
import helpers from '@/lib/helpers'
import draggable from 'vuedraggable'

function initSearch() {
  return {
    // TODO: pagination
    size: 10000,
    page: 1,
    // list mode sort option
    sortColumn: 'Name',
    sortColumnAsc: true, // false = sort descending
  }
}

export default {
  name: 'commonListing',
  components: {
    AssetColumnSelector,
    ElementListing,
    MessageModal,
    deleteMessageModal: MessageModal,
    ElementFormModal,
    NewAssetModal,
    SyncModal,
    ElementCard,
    draggable,
  },
  props: {
    dataType: String,
    dataElements: {
      type: Object,
      default: null,
    },
    selectableColumns: Array,
    sortableColumns: Object,
    defaultSortColumn: String,
    catalogueFields: Object,
    arrayFields: Array,
    enableEdition: Boolean, // in admin area only
  },
  data() {
    return {
      viewMode: 'list',
      //pager: {},
      //elements: [],
      search: initSearch(),
      // table columns
      selectedColumns: [],
      userMovedColumns: [],
      // MessageModalConfig
      modalTitle: '',
      modalMessage: '',
      modalCancel: false,
      okButtonText: 'Ok',
      cancel: false,
      // element modal
      elementModalMode: 'Add',
      elementModalTitle: '',
      showElementModal: false,
      editElementData: {},
      // delete confirmation
      selectedElementForDelete: null,
      // api form of dataType
      apiType: '',
      // new Asset Modal
      datasetData: {},
    }
  },
  computed: {
    isAdmin() {
      return this.$store.getters.isAdministrator
    },
    clsIconViewMode() {
      switch (this.viewMode) {
        case 'list':
          return 'icon-grid'
        case 'grid':
          return 'icon-list p-0'
      }
    },
    elements() {
      this.dataElements ? console.log(this.dataElements) : null
      return this.dataElements ? this.dataElements.results : []
    },
    pager() {
      return this.dataElements ? this.dataElements.pagination : {}
    },
    sortColumnIcons() {
      const columns = {}
      for (const col of Object.keys(this.sortableColumns)) {
        let icon
        if (this.search.sortColumn === col) {
          icon = this.search.sortColumnAsc ? 'icon-expand_less' : 'icon-expand_more'
        } else {
          icon = 'icon-unfold_more'
        }
        columns[col] = {
          icon,
        }
      }

      return columns
    },
    // Possible columns for list view mode
    displayedColumns() {
      const columns = {}
      for (const model of this.selectedColumns) {
        columns[model.label] = true
      }
      const displayedColumns = []
      for (let column of this.selectableColumns) {
        if (column in columns) {
          displayedColumns.push(column)
        }
      }
      return displayedColumns
    },
    allButFixedColumns() {
      return this.userMovedColumns.slice(1)
    },
    columnSelectors() {
      return this.selectableColumns.map((label) => new ColumnSelectorModel(label))
    },
    // Gather the parameters for the search query
    // Mainly for pagination
    getSearchParams() {
      return {
        size: this.search.size,
        from: (this.search.page - 1) * this.search.size,
        // Map label to API field
        sort_by: this.sortableColumns[this.search.sortColumn],
        order_by: this.search.sortColumnAsc ? 'asc' : 'desc',
      }
    },
    enableAddToElement() {
      // !this.enableEdition == no es admin.
      // only in not Admin area allow to add to element
      return this.dataType === 'Dataset' && !this.enableEdition
    },
  },
  methods: {
    //Toggle Grid or List mode
    toggleViewMode() {
      if (this.viewMode === 'grid') {
        this.viewMode = 'list'
      } else {
        this.viewMode = 'grid'
      }
    },
    // Show all columns
    showAllColumns() {
      this.selectedColumns = this.columnSelectors.slice()
      this.userMovedColumns = ['fixedcol'].concat(this.displayedColumns.slice())
    },
    // Used to toggle sort in list view mode
    toggleColumnSort(column) {
      if (this.search.sortColumn === column) {
        // Toggle sort direction
        this.search.sortColumnAsc = !this.search.sortColumnAsc
      } else {
        this.search.sortColumn = column
        this.search.sortColumnAsc = true
      }

      // Just call API here
      // TODO: put sort column options into route?
      this.$emit('getElements', this.dataType, this.getSearchParams)
    },
    // Make browser download a file from the AJAX response
    async downloadCsv() {
      const response = await api.downloadCsv(this.apiType)
      if (helpers.checkErrMessage(response)) {
        // a message error is received
        this.modalTitle = 'An Error Occurred'
        this.modalMessage = response.error //message
        this.modalCancel = false
        this.okButtonText = 'Ok'
        $('.message-modal').modal()
      } else {
        // Make browser download the response
        const blob = new Blob([response], { type: 'text/csv' })
        const a = document.createElement('a')
        a.style.display = 'none'
        const url = window.URL.createObjectURL(blob)
        a.href = url
        // the filename you want
        a.download = 'data.csv'
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }
    },
    syncModal() {
      Vue.nextTick(() => {
        $('.sync-modal').modal()
        this.$refs.syncModalRef.$refs.firstInputRef.focus()
        $('.sync-modal').on('hidden.bs.modal', () => {
          // if needs be do something here
        })
        $('.sync-modal').on('shown.bs.modal', () => {
          // this.$nextTick(() => this.$refs.syncModalRef.$refs.firstInputRef.focus())
        })
      })
    },
    // syncConfirmationModal() {
    //   //const response = await api.sync()
    //   this.modalTitle = 'Confirm Sync'
    //   this.modalMessage = 'Are you sure you want to sync database changes to Elasticsearch now?' //response
    //   this.okButtonText = 'Confirm'
    //   this.modalCancel = true
    //   $('.message-modal').modal()
    // },
    async sync(limit) {
      console.log('SYNC is launched with limit', limit)
      const response = await api.sync(limit)
      if (helpers.checkErrMessage(response)) {
        // a message error is received
        this.modalTitle = 'An Error Occurred'
        this.modalMessage = response.error //message
        this.modalCancel = false
        this.okButtonText = 'Ok'
        $('.message-modal').modal()
      }
    },
    addElement() {
      this.elementModalMode = 'Add'
      this.elementModalTitle = `Add ${this.dataType}`
      this.showElementModal = true
      Vue.nextTick(() => {
        $('.element-form-modal').modal()
        $('.element-form-modal').on('hidden.bs.modal', () => {
          this.formModalClosed()
        })
      })
    },
    editElement(data) {
      // console.log(data)
      this.elementModalMode = 'Edit'
      this.elementModalTitle = `Edit ${this.dataType} ${data.id}`
      this.showElementModal = true
      this.editElementData = data
      Vue.nextTick(() => {
        $('.element-form-modal').modal()
        $('.element-form-modal').on('hidden.bs.modal', () => {
          this.formModalClosed()
        })
      })
    },
    formModalClosed() {
      this.showElementModal = false
      this.editElementData = {}
    },
    async createOrEditNewElement(params) {
      console.log({ params })
      let response
      if (params.mode === 'Add') {
        response = await api.createNewElement(this.apiType, params.apiFields)
      } else if (params.mode === 'Edit') {
        response = await api.editElement(this.apiType, params.id, params.apiFields)
      }
      // console.log('RESPONSE IS', response)
      if (response != params.id && typeof response === 'object') {
        const id = params.mode == 'Edit' ? params.id : ''
        this.modalTitle = `Error in ${params.mode} ${this.dataType} ${id}`
        this.modalMessage = JSON.stringify(response.error)
        this.okButtonText = 'Ok'
        this.modalCancel = false
        $('.message-modal').modal()
      }
      this.$emit('getElements', this.dataType, this.getSearchParams)
    },
    confirmElementDeletion(data) {
      this.selectedElementForDelete = data
      this.modalTitle = 'Confirm Delete'
      this.modalMessage = `Are you sure you want to delete ${this.dataType} ${data.id}?`
      this.okButtonText = 'Delete'
      //this.showModal = true
      Vue.nextTick(() => {
        $('.delete-message-modal').modal('show')
      })
    },
    async deleteElement() {
      // console.log(`ELEMENT ${this.selectedElementForDelete.id} IS DELETED`)
      await api.deleteElement(this.apiType, this.selectedElementForDelete.id)
      this.selectedElementForDelete = null
      // SHOULD RELOAD AFTER DELETE
      this.$emit('getElements', this.dataType, this.getSearchParams)
    },
    openNewAssetModal(data) {
      // Open modal on next DOM update
      console.log('open new asset modal', data)
      this.datasetData = data
      Vue.nextTick(() => {
        $('.new-asset-modal').modal()
        this.$refs.newAssetRef.$refs.filesRef.focus()
        $('.new-asset-modal').on('hidden.bs.modal', () => {
          this.datasetData = {}
          console.log('cleared', this.datasetData)
        })
      })
    },
    showAssetSavedResult(msg) {
      this.modalTitle = `Uploading Files`
      this.modalMessage = msg
      this.okButtonText = 'Ok'
      this.modalCancel = false
      $('.message-modal').modal()
    },
  },
  async mounted() {
    this.search.sortColumn = this.defaultSortColumn
    // default to cards on mobile width (<991px)
    if (window.innerWidth < 991) {
      this.viewMode = 'grid'
    }
    this.apiType =
      this.dataType === 'Resource Type' ? 'resource-types' : this.dataType === 'Dataset' ? 'datasets' : 'file-format'
    this.$emit('getElements', this.dataType, this.getSearchParams)
    // Display all columns by default
    //  = async () => {
    //   var response = await this.data
    //   return response;
    // }
    //this.elements = data.results;
    // Set pager
    //this.pager = data.pagination;
    this.showAllColumns()
  },
  watch: {
    dataType() {
      this.showAllColumns()
      this.search.sortColumn = this.defaultSortColumn
      this.$emit('getElements', this.dataType, this.getSearchParams)
      this.apiType =
        this.dataType === 'Resource Type' ? 'resource-types' : this.dataType === 'Dataset' ? 'datasets' : 'file-format'
    },
    displayedColumns: function() {
      console.log('displayed here is', this.displayedColumns)
      console.log('usermoved here is', this.userMovedColumns)
      // Remove from usermoved, all cols that are not in displayed
      this.userMovedColumns = this.userMovedColumns.filter((label) => {
        return this.displayedColumns.includes(label) || label === 'Filename' || label === 'fixedcol'
      })
      console.log('usermoved here is', this.userMovedColumns)
      // Add (at the end) all cols that have been added
      const newCols = this.displayedColumns.filter((label) => !this.userMovedColumns.includes(label))
      console.log({ newCols })
      if (newCols) {
        this.userMovedColumns.splice(2, 0, ...newCols)
      }
    },
    isAdmin() {
      // reload elements if admin status changes
      this.$emit('getElements', this.dataType, this.getSearchParams)
    },
  },
}
</script>

<style lang="scss" scoped>
// It is needed to import all three basic bootstrap stuff again for the mixin (media-breakpoint-down) to work
@import '~bootstrap/scss/functions';
@import '~bootstrap/scss/variables';
@import '~bootstrap/scss/mixins';
@import '~@/styles/globals';

.elements__results-list {
  overflow-x: scroll;
  height: calc(100vh - 310px);
  // height: 100%;

  // &.maximized {
  //   height: calc(100vh - 100px);
  // }

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 7px;
    height: 7px;
  }

  // Overwrite default safari behavior to show scrollbars always
  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: rgba(0, 0, 0, 0.5);
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
    -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }
  &::-webkit-scrollbar-corner {
    background: #f0f0ef;
  }

  thead {
    white-space: nowrap;
  }
}

.elements__controls {
  position: sticky;
  top: 56px;
  background: $bg-light;
  z-index: 1000;
}

.elements__controls-show-all-columns {
  padding: 0 8px !important;
}

@include media-breakpoint-down(xs) {
  .elements__toggle-view {
    display: none;
  }
}
</style>
